import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { filter, map, switchMap, take } from 'rxjs/operators';
import { CognitoAuthFacade } from '@cpd-app/state/cognito-auth';
import { Observable, of } from 'rxjs';

import { LoginDialogComponent } from '../../components/login-dialog/login-dialog.component';
import { LoginDialogView } from '../../components/login-dialog/login-dialog-view.model';
import { LandedMagicLink } from './magic-link-landing.service';

@Injectable({
  providedIn: 'root'
})
export class AccountCreationPromptService {
  private dialogRef: MatDialogRef<LoginDialogComponent>;
  constructor(
    private dialog: MatDialog,
    private cognitoAuthFacade: CognitoAuthFacade
  ) {}

  public openAccountCreationDialogWithConfirm(
    context?: string
  ): Observable<boolean> {
    return this.cognitoAuthFacade.isAuthenticated$.pipe(
      switchMap((isAuthenticated) => {
        if (isAuthenticated) {
          return of(true);
        }

        return this.openLoginDialog();
      })
    );
  }

  public openAccountCreationDialog(context?: string): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }

    this.cognitoAuthFacade.isAuthenticated$
      .pipe(
        filter((isAuthenticated) => !isAuthenticated),
        take(1),
        switchMap(() => this.openLoginDialog(context === 'skills-survey'))
      )
      .subscribe();
  }

  /**
   * AUTH.7. The page opened from an emailed link. Show "Continue to sign in"
   * so the link is spent by a person's press, not by the page loading.
   */
  public openMagicLinkContinue(link: LandedMagicLink): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }

    this.openLoginDialog(false, { startAt: 'continue', magicLink: link });
  }

  /**
   * AUTH.9. Open the dialog on a given card: the "This link has expired" card
   * for an old goals report link, or "Add your email" from its banner.
   */
  public openAt(startAt: LoginDialogView): void {
    if (this.dialogRef) {
      this.dialogRef.close();
    }

    this.openLoginDialog(false, { startAt });
  }

  private openLoginDialog(
    openViaIntro?: boolean,
    extra: { startAt?: LoginDialogView; magicLink?: LandedMagicLink } = {}
  ): Observable<boolean> {
    // 01-account acceptance criterion 0: the dialog is 645px. The width and the
    // fixed 750px height that were here overrode cpd-dialog-container2's own
    // size, and the fixed height clipped the taller registration views rather
    // than letting the dialog grow to what it holds.
    this.dialogRef = this.dialog.open(LoginDialogComponent, {
      ariaLabel: 'Sign in',
      width: '645px',
      maxHeight: '90vh',
      // MOBILE.1 D6. `vh` on iOS Safari is the height with the toolbars
      // hidden, so a dialog at 90vh was taller than the visible screen and its
      // top, close button included, sat under the address bar. The pane class
      // caps it at the small viewport where the browser supports `svh`.
      panelClass: 'cpd-login-pane',
      // QA.21: the emailed-link dialog holds the only copy of the link, so
      // Escape or a click outside must not drop it; its X still closes it.
      disableClose: !!extra.magicLink,
      data: {
        openViaIntro: openViaIntro,
        ...extra
      }
    });

    return this.dialogRef.afterClosed().pipe(map(() => true));
  }
}
